import { fetchMethods } from '@/types/FetchService.types';

import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { IWebhook } from '@/features/webhooks/store/webhook.model';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
export interface IWebhooksResponse {
  hooks: IWebhook[];
}
export interface IWebhookEventsResponse {
  events: string[];
}
export const getWebhooksUrl = () => {
  const config = getStaticDashboardConfig();
  return `${config.api.webhooks.path}/settings/hooks`;
};
export class WebhookService {
  static queryKey = 'Webhooks';
  static searchWebhooks = async (): Promise<IWebhook[]> => {
    try {
      const url = getWebhooksUrl();
      const webhooksResponse: Response = await fetchQuery([this.queryKey, { method: fetchMethods.GET }], url, {
        headers: createHttpHeader(),
      });

      return await decryptTreezorResponse<IWebhook>(webhooksResponse, {
        shouldSkipEncryption: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static addWebhook = async (webhookRequest: IWebhook): Promise<IWebhook> => {
    try {
      const url = getWebhooksUrl();
      const webhooksResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.POST, params: webhookRequest }],
        url,
        {
          method: 'POST',
          headers: createHttpHeader(),
          body: JSON.stringify(webhookRequest),
        },
      );

      const webhooks = await decryptTreezorResponse<IWebhook>(webhooksResponse, {
        shouldSkipEncryption: true,
      });
      return webhooks.find((webhook) => webhook.url === webhookRequest.url) || webhookRequest;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static searchWebhookEvents = async (webhook: IWebhook): Promise<IWebhook> => {
    try {
      if (webhook.status !== 'ENABLED') return Promise.resolve({ ...webhook, events: [] });
      const baseUrl = getWebhooksUrl();
      const webhooksResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'search', id: webhook.uuid }],
        `${baseUrl}/${webhook.uuid}/events`,
        {
          headers: createHttpHeader(),
        },
      );

      const events = await decryptTreezorResponse<string>(webhooksResponse, {
        shouldSkipEncryption: true,
      });
      return { ...webhook, events };
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static updateEvents = async (webhook: IWebhook, events: string[]): Promise<IWebhook> => {
    try {
      const baseUrl = getWebhooksUrl();
      const webhooksResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.POST, id: webhook.uuid, params: events }],
        `${baseUrl}/${webhook.uuid}/events`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ events }),
        },
      );

      const webhookEvents = await decryptTreezorResponse<string>(webhooksResponse, {
        shouldSkipEncryption: true,
      });
      return { ...webhook, events: webhookEvents };
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static removeWebhook = async (webhook: IWebhook): Promise<string> => {
    try {
      const baseUrl = getWebhooksUrl();
      const webhooksResponse = await fetchQuery(
        [this.queryKey, { method: 'DELETE', id: webhook.uuid }],
        `${baseUrl}/${webhook.uuid}`,
        {
          headers: createHttpHeader(),
        },
      );

      await decryptTreezorResponse(webhooksResponse, { shouldSkipEncryption: true });
      return webhook.uuid as string;
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
